import React, { useEffect, useState } from 'react'
import Modal, {
  ModalFooter,
  ModalHeader,
  ModalScrollContent,
} from '@app/components/Modal'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import theme from '@app/ui/theme'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import * as S from './AddTeesModal.styles'
import {
  isTeeRowComplete,
  teeFromRow,
  TeeGrid,
  TeeRow,
  TeeRowHeader,
  TeeRowValue,
} from './TeeRow'
import {
  PresetCard,
  PresetCheck,
  PresetHint,
  PresetLabel,
  PresetText,
} from './HoleRangeModal.styles'
import {
  hasStandardSet,
  TeeSetKind,
  TEE_COUNTS,
  TEE_SET_KINDS,
  teeTemplates,
} from '../teeSets'
import { Tee } from '../manualSlopeTableService'

type Props = {
  isOpen: boolean
  onClose: () => void
  onAdd: (tees: Tee[]) => void
}

const DEFAULT_KIND: TeeSetKind = 'standard'
const DEFAULT_COUNT = 4

const toRows = (
  kind: TeeSetKind,
  count: number,
  previous: TeeRowValue[] = [],
  // Changing the count carries the names along only when they were typed in
  // by hand. Names that came from a set belong to that set, so a count the
  // set does not cover starts blank. Changing the set never carries them: the
  // new set names its tees, or leaves them blank to be typed again.
  keepTitles = false
) =>
  teeTemplates(kind, count).map((template, index) => ({
    title: template.title || (keepTitles ? previous[index]?.title ?? '' : ''),
    color: template.color,
    // Changing the set keeps the numbers already typed in, so picking the
    // wrong count first does not cost them.
    courseRating: previous[index]?.courseRating ?? '',
    slopeRating: previous[index]?.slopeRating ?? '',
  }))

const AddTeesModal: React.FC<Props> = ({ isOpen, onClose, onAdd }) => {
  const [kind, setKind] = useState<TeeSetKind>(DEFAULT_KIND)
  const [count, setCount] = useState(DEFAULT_COUNT)
  const [rows, setRows] = useState<TeeRowValue[]>(
    toRows(DEFAULT_KIND, DEFAULT_COUNT)
  )
  // Only marks the fields red once adding has been tried, so the form does not
  // open covered in errors.
  const [showErrors, setShowErrors] = useState(false)

  // Opening the modal starts from a clean set rather than from whatever was
  // typed and abandoned last time.
  useEffect(() => {
    if (!isOpen) return
    setKind(DEFAULT_KIND)
    setCount(DEFAULT_COUNT)
    setRows(toRows(DEFAULT_KIND, DEFAULT_COUNT))
    setShowErrors(false)
  }, [isOpen])

  const selectKind = (value: TeeSetKind) => {
    setKind(value)
    setRows((previous) => toRows(value, count, previous))
  }

  const selectCount = (value: number) => {
    setCount(value)
    setRows((previous) =>
      toRows(kind, value, previous, !hasStandardSet(kind, count))
    )
  }

  const updateRow = (index: number, update: Partial<TeeRowValue>) =>
    setRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...update } : row
      )
    )

  const isComplete = rows.length > 0 && rows.every(isTeeRowComplete)

  const addTees = () => {
    if (!isComplete) {
      setShowErrors(true)
      return
    }
    onAdd(rows.map(teeFromRow))
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="640px" height="auto">
      <ModalHeader>
        <S.Title>Add several tees</S.Title>
        <S.Subtitle>
          Pick the set the course is laid out in, then fill in the ratings.
        </S.Subtitle>
      </ModalHeader>
      <ModalScrollContent>
        <S.Body>
          <S.Section>
            <S.SectionLabel>Tee set</S.SectionLabel>
            <S.KindGrid>
              {TEE_SET_KINDS.map(({ value, label, hint }) => (
                <PresetCard
                  key={value}
                  type="button"
                  isSelected={kind === value}
                  onClick={() => selectKind(value)}
                >
                  <PresetText>
                    <PresetLabel>{label}</PresetLabel>
                    <PresetHint>{hint}</PresetHint>
                  </PresetText>
                  <PresetCheck isSelected={kind === value}>
                    {kind === value && (
                      <FeatherIcon
                        name="check"
                        size={12}
                        color={theme.colors.white}
                      />
                    )}
                  </PresetCheck>
                </PresetCard>
              ))}
            </S.KindGrid>
          </S.Section>

          <S.Section>
            <S.SectionLabel>Number of tees</S.SectionLabel>
            <S.CountRow>
              {TEE_COUNTS.map((value) => (
                <S.CountChip
                  key={value}
                  type="button"
                  isSelected={count === value}
                  onClick={() => selectCount(value)}
                >
                  {value}
                </S.CountChip>
              ))}
            </S.CountRow>
          </S.Section>

          <S.Section>
            <S.SectionLabel>Ratings</S.SectionLabel>
            <TeeGrid>
              <TeeRowHeader />
              {rows.map((row, index) => (
                <TeeRow
                  key={index}
                  label={`Tee ${index + 1}`}
                  value={row}
                  showErrors={showErrors}
                  onChange={(update) => updateRow(index, update)}
                />
              ))}
            </TeeGrid>
            {showErrors && !isComplete && (
              <S.Hint>
                Every tee needs a name, a course rating and a slope rating.
              </S.Hint>
            )}
          </S.Section>
        </S.Body>
      </ModalScrollContent>
      <ModalFooter>
        <RoundedButton
          title="Cancel"
          onPress={onClose}
          style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.secondary}`,
            color: theme.colors.secondary,
          }}
          textStyle={{ color: theme.colors.secondary }}
        />
        <RoundedButton
          title={`Add ${rows.length} ${rows.length === 1 ? 'tee' : 'tees'}`}
          onPress={addTees}
          style={{ backgroundColor: theme.colors.secondary2 }}
        />
      </ModalFooter>
    </Modal>
  )
}

export default AddTeesModal
